import { Body2, Normal2 } from 'components/Typography';
import Link from 'next/link';
import { formatAddress } from 'structure/user/WalletConnect';
import Image from 'next/image';
import {
  getNetworkIconUrlByChaiId,
  getStaticAssetUrl,
  getTokenIconUrl,
} from 'utils/url';
import { showWalletConnectModal } from 'store/features/wallet/walletSlice';
import { useTranslation } from 'react-i18next';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { useWalletClient } from 'wagmi';
import { useAppDispatch } from 'store/hooks';
import React from 'react';
import { TokenImage } from 'components/TokenImage';

interface AddToMetamaskParams {
  chainId: number;
  address: `0x${string}`;
  symbol: string;
  decimals: number;
  image: string;
}

interface TokenAddressProps {
  chainId: string;
  token: {
    title: string;
    address: string;
    url: string;
  };
}

export const TokenAddressItem = (props: TokenAddressProps) => {
  const { t } = useTranslation();
  const toastV2 = useToast();
  const walletClient = useWalletClient();
  const dispatch = useAppDispatch();

  const { chainId, token } = props;

  const handleAddTokenToMetamask =
    (params: AddToMetamaskParams) => async () => {
      try {
        if (!walletClient.data) {
          dispatch(showWalletConnectModal(true));
          return;
        }
        const currentChainId = await walletClient.data.getChainId();
        if (currentChainId !== params.chainId) {
          try {
            await walletClient.data.switchChain({ id: params.chainId });
          } catch (error) {
            console.error('Failed to switch network:', error);
            toastV2({
              title: t('common:ERROR_MESSAGES.NETWORK_SWITCH_ERROR'),
              type: 'error',
            });
            return;
          }
        }
        await walletClient.data.watchAsset({
          type: 'ERC20',
          options: {
            address: params.address,
            symbol: params.symbol,
            decimals: params.decimals,
            image: params.image,
          },
        });
      } catch (error) {
        console.error(error);
      }
    };

  const handleCopy = (address: `0x${string}`) => () => {
    navigator.clipboard.writeText(address).then(() => {
      toastV2({
        title: t('common:ERROR_MESSAGES.SUCCESS_COPIED_TEXT'),
        type: 'success',
      });
    });
  };

  return (
    <div className='flex gap-2'>
      <div className='flex items-center gap-2 text-sm'>
        <Normal2 className='text-xs font-bold text-white-400 md:text-sm'>
          Contract:
        </Normal2>
        <TokenImage
          symbol={'ETH'}
          src={getNetworkIconUrlByChaiId(Number(chainId))}
          alt={token.title}
          width={24}
          height={24}
        />
        <Link href={token.url} target='_blank'>
          <Body2 className='text-[0.75rem] font-normal text-white md:text-sm'>
            <span className='xl:hidden'>
              {formatAddress(token.address, true)}
            </span>
            <span className='hidden xl:inline'>{token.address}</span>
          </Body2>
        </Link>
      </div>
      <div className='flex gap-2'>
        <Image
          alt='copy'
          width={16}
          height={16}
          className='cursor-pointer'
          src={getStaticAssetUrl('/sfi/static/image/icons/content_copy.svg')}
          onClick={handleCopy(token.address as `0x${string}`)}
        />
        <button
          className='flex cursor-pointer items-center space-x-2 rounded-full text-white'
          onClick={handleAddTokenToMetamask({
            chainId: Number(chainId),
            address: token.address as `0x${string}`,
            symbol: 'SFI',
            decimals: 18,
            image: getTokenIconUrl('SFI'),
          })}
        >
          <Image
            src={getStaticAssetUrl('/sfi/static/image/wallets/metamask.svg')}
            alt='meta-mast'
            width={16}
            height={16}
          />
        </button>
      </div>
    </div>
  );
};
